/* eslint-disable react/destructuring-assignment */
import { fetchChildrenTagByRemoteId, fetchStartup } from 'features/innovations/business/Startup.api';
import { InnovationDetailLayout } from 'features/innovations/components/InnovationDetailLayout/InnovationDetailLayout.component';
import Error from 'pages/_error';
import { InnovationTagEnum, Startup, Tag } from 'pleinchamp-api-client';
import React, { createContext } from 'react';
import { usePlcApiCall } from '@api/business/api.hook';
import { ApiStatusCode } from '@utils/api.utils';
import { isServer } from '@utils/server';
import { defaultNamespaces } from '@utils/strings';
import { castUrlParamToString, return404 } from '@utils/url';
import { PlcNextPage, PlcPageContextProps } from 'features/business/app.types';

interface Props {
  startup: Startup;
  domains: Tag[];
  sectors: Tag[];
  stakes: Tag[];
  isPreview: boolean;
  slug: string;
}

export const InnovationContext = createContext<PlcPageContextProps<Props>>({
  domains: { isValidating: false },
  isPreview: { isValidating: false },
  sectors: { isValidating: false },
  slug: { isValidating: false },
  stakes: { isValidating: false },
  startup: { isValidating: false },
});

const InnovationPage: PlcNextPage<Partial<Props>> = props => {
  const slug = { data: props.slug, isValidating: false };

  const startup = usePlcApiCall(fetchStartup, [props.slug || ''], {
    initialData: props.startup,
  });

  const domains = usePlcApiCall(fetchChildrenTagByRemoteId, [InnovationTagEnum.domain, 1], {
    initialData: props.domains,
  });
  const sectors = usePlcApiCall(fetchChildrenTagByRemoteId, [InnovationTagEnum.sector], {
    initialData: props.sectors,
  });
  const stakes = usePlcApiCall(fetchChildrenTagByRemoteId, [InnovationTagEnum.stake], {
    initialData: props.stakes,
  });

  if (!startup.isValidating && startup.error?.response?.status === ApiStatusCode.notFound) {
    return <Error errorCode={ApiStatusCode.notFound} />;
  }

  return (
    <InnovationContext.Provider
      value={{
        domains,
        isPreview: { data: props.isPreview, isValidating: false },
        sectors,
        slug,
        stakes,
        startup,
      }}
    >
      {!startup.isValidating && <InnovationDetailLayout />}
    </InnovationContext.Provider>
  );
};

InnovationPage.getInitialProps = async ctx => {
  const { query } = ctx;
  const slug = castUrlParamToString(query.slug);
  if (!slug) {
    return return404(ctx);
  }

  const pageDefaultState = {
    namespacesRequired: [...defaultNamespaces, 'innovations'],
    slug,
  };

  if (!isServer()) {
    return pageDefaultState;
  }

  const props = await Promise.all([
    fetchStartup(slug),
    fetchChildrenTagByRemoteId(InnovationTagEnum.domain, 1),
    fetchChildrenTagByRemoteId(InnovationTagEnum.stake),
    fetchChildrenTagByRemoteId(InnovationTagEnum.sector),
  ])
    .then(([startupContent, domains, stakes, sectors]) => ({
      ...pageDefaultState,
      domains,
      sectors,
      stakes,
      startupContent,
    }))
    .catch(() => pageDefaultState);

  return props;
};
export default InnovationPage;
